<!--
/**
 * @component PageChangeSpace
 * @description Page de transition lors du changement d'espace entreprise
 * @example
 * <PageChangeSpace />
 */
-->
<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import { useRootStore } from "@core/stores/root.store"
import type { Services } from "@services"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            welcome: "Bienvenue 👋",
            changing: "Changement d'espace en cours...",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const authStore = useAuthStore()
const configStore = useConfigStore()
const rootStore = useRootStore()
const route = useRoute()
const router = useRouter()
const { $updateCurrentUser } = inject<Services>("services")!

// ================================
// COMPUTED
// ================================

/**
 * Logo du scope actuel
 */
const getScopeLogo = computed(() => {
    return configStore.getScopeLogoFull || configStore.getScopeLogoFullWhite
})

/**
 * Nom du scope pour l'alt de l'image
 */
const getScopeName = computed(() => {
    return configStore.getScopeBrand || configStore.getScopeName || "Homeys"
})

// ================================
// LIFECYCLE
// ================================

/**
 * Au montage du composant, effectue le changement d'entreprise
 */
onMounted(async () => {
    const companyId = route.query?.company as string

    // Si pas d'ID d'entreprise, rediriger vers l'accueil
    if (!companyId) {
        await router.push("/")
        return
    }

    try {
        // Mettre à jour last_login avec le nouvel _id_company
        await $updateCurrentUser({
            last_login: {
                _id_company: companyId,
            },
        })

        // Recharger les informations utilisateur
        await authStore.fetchUser()

        // Reset tous les stores sauf auth (pour garder le token et les infos user)
        rootStore.resetAllStores(["auth"])

        // Recharger le scope avec la nouvelle entreprise
        await configStore.fetchScope()

        // Rediriger vers l'accueil
        await router.push("/")
    } catch (error) {
        console.error("Erreur lors du changement d'entreprise:", error)
        // En cas d'erreur, rediriger quand même vers l'accueil
        await router.push("/")
    }
})
</script>

<template>
    <!-- ================================ -->
    <!-- PAGE CHANGEMENT D'ESPACE -->
    <!-- ================================ -->
    <div
        class="min-h-screen w-full flex flex-col items-center justify-center gap-20 bg-gradient-to-br from-surface-50 to-surface-100"
    >
        <!-- Logo -->
        <img
            v-if="getScopeLogo"
            :src="getScopeLogo"
            :alt="getScopeName"
            class="h-16 object-contain"
        />

        <!-- Contenu -->
        <div class="flex flex-col items-center justify-center gap-6">
            <h2 class="text-xl font-normal text-surface-600">
                {{ t("changing") }}
            </h2>
            <BaseProgressSpinner
                style="width: 50px; height: 50px"
                stroke-width="4"
            />
        </div>
    </div>
</template>
